import isPlainObject from 'lodash.isplainobject'
import isArray from 'lodash/isArray'

import { CREATE, DELETE, GET_LIST, GET_ONE, GET_MANY, UPDATE } from '../constants'

import fetch from './fetch'
import PeractoDocument from './document'
import { decodeJwt } from '../util/JwtDecode'

const PUBLIC_URLS = ['/forgotten-password', '/set-password']

const documentsCache = new Map()

/**
 * @param {Object} document
 * @param {bool} clone
 * @param {bool} addToCache
 *
 * @return {PeractoDocument}
 */
export const transformResponseToDocument = (document, clone = true, addToCache = true) => {
    if (clone) {
        // deep clone documents
        document = JSON.parse(JSON.stringify(document))
    }

    // The main document is a JSON-LD document, convert it and store it in the cache
    if (document['@id']) {
        document = new PeractoDocument(document)
    }

    // Replace embedded objects by their IRIs, and store the object itself in the cache to reuse without issuing new HTTP requests.
    Object.keys(document).forEach(key => {
        // to-many
        if (
            Array.isArray(document[key]) &&
            document[key].length &&
            isPlainObject(document[key][0]) &&
            document[key][0]['@id']
        ) {
            document[key] = document[key].map(obj => {
                if (addToCache) {
                    documentsCache[obj['@id']] = transformResponseToDocument(obj, false, false)
                }

                return obj
            })
        }
    })

    return document
}

export default (api, options = {}, client = fetch) => {
    const { entrypoint } = api

    const convertRequest = (type, resource, params, headers = {}) => {
        const entrypointUrl = new URL(entrypoint, window.location.href)
        const collectionUrl = new URL(`${entrypoint}/${resource}`, entrypointUrl)
        const itemUrl = new URL(params.id, entrypointUrl)

        switch (type) {
            case CREATE:
                return Promise.resolve({
                    options: {
                        body: JSON.stringify(params.data),
                        method: 'POST',
                        headers,
                    },
                    url: collectionUrl,
                })

            case DELETE:
                return Promise.resolve({
                    options: {
                        method: 'DELETE',
                        headers,
                    },
                    url: itemUrl,
                })

            case GET_LIST: {
                const {
                    pagination: { page, perPage } = { page: 1 },
                    sort: { field, order } = {},
                    name,
                    label,
                } = params

                if (order) collectionUrl.searchParams.set(`order[${field}]`, order)
                if (page) collectionUrl.searchParams.set('page', page)
                if (perPage) collectionUrl.searchParams.set('itemsPerPage', perPage)
                if (name) collectionUrl.searchParams.set('name', name)
                if (label) collectionUrl.searchParams.set('label', label)

                if (params.filter) {
                    Object.keys(params.filter).forEach(key => {
                        const filterValue = params.filter[key]

                        if (isArray(filterValue) && filterValue?.length > 0) {
                            filterValue.forEach(value => {
                                collectionUrl.searchParams.append(`${key}[]`, value)
                            })
                            return
                        }

                        if (!isPlainObject(filterValue)) {
                            collectionUrl.searchParams.set(key, params.filter[key])
                            return
                        }

                        Object.keys(filterValue).forEach(subKey => {
                            collectionUrl.searchParams.set(`${key}[${subKey}]`, filterValue[subKey])
                        })
                    })
                }

                return Promise.resolve({
                    options: {
                        headers,
                    },
                    url: collectionUrl,
                })
            }

            case GET_ONE:
            case GET_MANY:
                return Promise.resolve({
                    options: {
                        headers,
                    },
                    url: itemUrl,
                })

            case UPDATE:
                return Promise.resolve({
                    options: {
                        body: JSON.stringify(params.data),
                        method: 'PUT',
                        headers,
                    },
                    url: itemUrl,
                })

            default:
                throw new Error(`Unsupported fetch action type ${type}`)
        }
    }

    const convertResponse = (type, resource, response) => {
        if (response.status === 204) {
            return Promise.resolve({ data: {} })
        }

        switch (type) {
            case GET_LIST:
                // case GET_MANY_REFERENCE:
                return Promise.resolve(
                    response.json['hydra:member'].map(transformResponseToDocument)
                ).then(data => ({
                    data,
                    total: response.json['hydra:totalItems'],
                }))

            case DELETE:
                return Promise.resolve({ data: { id: null } })

            case GET_MANY:
                return Promise.resolve(
                    response.json['hydra:member'].map(transformResponseToDocument)
                ).then(data => ({ data, response }))

            default:
                return Promise.resolve(transformResponseToDocument(response.json)).then(data => ({
                    data,
                    response,
                }))
        }
    }

    const revalidateUser = async headers => {
        const claims = localStorage.getItem('claims')
        if (!claims) {
            clearLocalStorage()
            window.location.href = '/login'
            throw { status: 401, message: 'Claims could not be found, refreshing' }
        }
        const expiryDate = localStorage.getItem('expires_at')
        if (!expiryDate) return

        // to convert php time
        const parsedDate = Number(expiryDate) * 1000

        const now = new Date().getTime()

        if (parsedDate < now) {
            console.log('revalidating')
            const endpoint = `${process.env.REACT_APP_API ||
                window.Cypress?.env('api')}/token/refresh`

            try {
                await fetch(endpoint, {
                    method: 'POST',
                    body: JSON.stringify({
                        refresh_token: localStorage.getItem('refresh_token'),
                    }),
                    headers,
                })
            } catch (e) {
                if (e.response.status === 200) {
                    const json = await e.response.json()

                    const claims = decodeJwt(json.token)

                    localStorage.setItem('token', json.token)
                    localStorage.setItem('claims', JSON.stringify(claims))
                    localStorage.setItem('expires_at', claims.exp)
                    localStorage.setItem('refresh_token', json.refresh_token)
                } else if (e.response.status === 401) {
                    clearLocalStorage()
                    window.location.href = '/login'
                }
            }
        }
    }

    const clearLocalStorage = () => {
        localStorage.removeItem('token')
        localStorage.removeItem('claims')
        localStorage.removeItem('expires_at')
        localStorage.removeItem('refresh_token')
    }

    const { user, onApiError = null } = options

    const fetchApi = async (type, resource, params, headers) => {
        try {
            if (!PUBLIC_URLS.includes(window.location.pathname)) {
                await revalidateUser(headers)
            }

            const { url, options } = await convertRequest(type, resource, params, headers)

            const response = await client(url, { ...options, user })
            return convertResponse(type, resource, response)
        } catch (e) {
            const err = {
                api,
                error: e,
                response: e.response,
                status: e.response ? e.response.status : null,
            }

            onApiError(err)

            if (e?.response?.status === 401 && !PUBLIC_URLS.includes(window.location.pathname)) {
                clearLocalStorage()
                window.location.href = '/login'
            }
            throw err
        }
    }

    return fetchApi
}
